// ---------------------------------------------------------------------------
// SettingsView — edit displayName / description, delete a workspace.
// Rendered inside the drawer. `name` is immutable per spec.
// ---------------------------------------------------------------------------

import {
  Button,
  Card,
  Divider,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Textarea,
  Toolbar,
} from '@fluentui/react-components';
import {
  bundleIcon,
  Delete20Filled,
  Delete20Regular,
} from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { config } from '../../config.js';
import { useWorkspaceContext } from '../../context/WorkspaceContext.js';
import { useWorkspaceDrawer } from '../../context/WorkspaceDrawerContext.js';
import {
  DataLakeEntitlementGuidance,
  PrivateWorkspaceAccessSection,
  PrivateWorkspaceConversionSection,
  PrivateWorkspaceDatasetDetails,
  isDataLakeEntitlementRequiredMessage,
  isWorkspaceCreator,
} from '../../features/private-workspaces/index.js';
import { useAuth } from '../../hooks/useAuth/useAuth.js';
import { useWorkspaces } from '../../hooks/useWorkspaces/useWorkspaces.js';
import { WorkspaceConcurrencyError } from '../../services/workspaceService.interface.js';
import { WorkspaceService } from '../../services/workspaceService.js';
import { useUserBi } from '../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  ActionOutcome,
  EntityType,
  getOperationActionScenario,
  OperationType,
  PanelRegion,
  PanelType,
} from '../../telemetry/userbi.js';
import type { Workspace } from '../../types/workspace.js';

import { DeleteWorkspaceDialog } from './DeleteWorkspaceDialog.js';
import { useStyles } from './WorkspaceDrawer.style.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const DeleteIcon = bundleIcon(Delete20Filled, Delete20Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

const PUBLISHING_APPLICATIONS_HINT =
  'Comma-separated application IDs allowed to publish evaluations.';

function dashboardLink(id: string | null | undefined): string {
  return id
    ? `${config.teamsPulseBaseUrl.replace(/\/$/, '')}/dashboards/${encodeURIComponent(id)}`
    : '';
}

function dashboardIdFromLink(link: string): string | null {
  if (link === '') return '';
  try {
    const url = new URL(link);
    const base = new URL(config.teamsPulseBaseUrl);
    const match = /^\/(?:embed\/)?dashboards\/([A-Za-z0-9_-]{1,128})\/?$/.exec(
      url.pathname,
    );
    if (url.origin === base.origin && !url.username && !url.password && match) {
      return match[1] ?? null;
    }
  } catch {
    // The form displays the validation error when the user saves.
  }
  return null;
}

function parseApplicationIds(value: string): string[] {
  return [
    ...new Set(
      value
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean),
    ),
  ];
}

export interface SettingsViewProps {
  name: string;
}

export function SettingsView({ name }: SettingsViewProps) {
  const styles = useStyles();
  const { refresh } = useWorkspaces();
  const { activeName, setActiveName } = useWorkspaceContext();
  const { open, close } = useWorkspaceDrawer();
  const httpClient = useHttpClient();
  const service = useMemo(() => new WorkspaceService(httpClient), [httpClient]);
  const { recordAction } = useUserBi();
  const { account } = useAuth();

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [dashboardUrl, setDashboardUrl] = useState('');
  const [publisherAppIds, setPublisherAppIds] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasConflict, setHasConflict] = useState(false);
  const [showConversion, setShowConversion] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);

  const loadWorkspace = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setHasConflict(false);
    service
      .get(name)
      .then((w) => {
        if (cancelled) return;
        setWorkspace(w);
        setDisplayName(w.displayName ?? '');
        setDescription(w.description ?? '');
        setDashboardUrl(dashboardLink(w.teamsPulseDashboardId));
        setPublisherAppIds(w.accessControl?.publisherAppIds.join(', ') ?? '');
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(
          e instanceof Error ? e.message : 'Failed to load workspace',
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [name, service]);

  useEffect(() => loadWorkspace(), [loadWorkspace]);

  const dirty =
    workspace !== null &&
    ((workspace.displayName ?? '') !== displayName ||
      (workspace.description ?? '') !== description ||
      dashboardLink(workspace.teamsPulseDashboardId) !== dashboardUrl ||
      (workspace.accessControl?.publisherAppIds.join(', ') ?? '') !==
        publisherAppIds);
  const canManage =
    workspace?.accessControl == null ||
    workspace.effectivePermission === 'manageAccess';
  const canConvert =
    workspace !== null &&
    workspace.accessControl == null &&
    isWorkspaceCreator(workspace, [account?.username, account?.localAccountId]);
  const isConverting = canConvert && showConversion;

  const handleSave = async () => {
    if (!workspace || !dirty || workspace.etag == null) return;
    const dashboardId = dashboardIdFromLink(dashboardUrl.trim());
    if (dashboardId === null) {
      setSaveError(
        'Enter a dashboard URL from the configured TeamsPulse site, without query parameters or a fragment.',
      );
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      recordAction({
        panelType: PanelType.WorkspaceDrawer,
        panelRegion: PanelRegion.Overlay,
        actionScenario: getOperationActionScenario(
          EntityType.Workspace,
          OperationType.Update,
        ),
        actionScenarioType: OperationType.Update,
        actionGesture: 'click',
        actionOutcome: ActionOutcome.Requested,
      });
      const updated = await service.patch(
        workspace.name,
        {
          displayName: displayName.trim(),
          description: description.trim(),
          ...(dashboardLink(workspace.teamsPulseDashboardId) !== dashboardUrl
            ? { teamsPulseDashboardId: dashboardId }
            : {}),
          ...(workspace.accessControl === null ||
          workspace.accessControl === undefined
            ? {}
            : {
                accessControl: {
                  publisherAppIds: parseApplicationIds(publisherAppIds),
                },
              }),
        },
        workspace.etag,
      );
      if (
        dashboardLink(workspace.teamsPulseDashboardId) !== dashboardUrl &&
        (updated.teamsPulseDashboardId ?? '') !== dashboardId
      ) {
        throw new Error(
          'The workspace API did not retain the TeamsPulse dashboard. Deploy the updated API before linking a dashboard.',
        );
      }
      setWorkspace(updated);
      setDashboardUrl(dashboardLink(updated.teamsPulseDashboardId));
      await refresh();
    } catch (e) {
      if (e instanceof WorkspaceConcurrencyError) {
        setHasConflict(true);
        setSaveError(
          'This workspace changed after you opened settings. Reload it before saving again.',
        );
      } else {
        setSaveError(e instanceof Error ? e.message : 'Failed to save changes');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleted = async (deletedName: string) => {
    setDeleteOpen(false);
    if (activeName === deletedName) setActiveName(null);
    await refresh();
    open({ kind: 'list' });
  };

  if (loading) {
    return (
      <Card className={styles.body}>
        <Spinner label="Loading workspace…" />
      </Card>
    );
  }

  if (loadError !== null || workspace === null) {
    const isEntitlementRequired =
      loadError !== null && isDataLakeEntitlementRequiredMessage(loadError);
    return (
      <Card className={styles.body}>
        {isEntitlementRequired ? (
          <DataLakeEntitlementGuidance />
        ) : (
          <Text className={styles.fieldError}>
            {loadError ?? 'Workspace not found'}
          </Text>
        )}
        <Toolbar className={styles.actions}>
          <Button appearance="secondary" onClick={() => open({ kind: 'list' })}>
            Back to list
          </Button>
        </Toolbar>
      </Card>
    );
  }

  return (
    <Card className={styles.body}>
      <Field
        label="Name"
        hint="Name is immutable. To rename, delete and recreate."
      >
        <Input id="workspace-name-ro" value={workspace.name} disabled />
      </Field>

      <Field label="Display name">
        <Input
          id="workspace-display"
          aria-label="Display name"
          value={displayName}
          disabled={!canManage}
          onChange={(_e, data) => setDisplayName(data.value)}
        />
      </Field>

      <Field label="Description">
        <Textarea
          id="workspace-description"
          aria-label="Description"
          value={description}
          disabled={!canManage}
          rows={3}
          onChange={(_e, data) => setDescription(data.value)}
        />
      </Field>

      <Field
        label="TeamsPulse dashboard URL"
        hint="The Trends page uses this dashboard. URL filters and tabs are not saved. Leave blank for the default dashboard. Private workspaces require Owner access to edit."
      >
        <Input
          aria-label="TeamsPulse dashboard URL"
          value={dashboardUrl}
          disabled={!canManage}
          onChange={(_event, data) => setDashboardUrl(data.value)}
        />
      </Field>

      {workspace.accessControl !== null &&
        workspace.accessControl !== undefined && (
          <>
            <Divider />
            <PrivateWorkspaceDatasetDetails
              datasetId={workspace.accessControl.aclAnchorDatasetId}
            />
            <PrivateWorkspaceAccessSection
              anchorDatasetId={workspace.accessControl.aclAnchorDatasetId}
              readOnly={!canManage}
            />
            <Divider />
            <Text weight="semibold" size={400}>
              Publishing applications
            </Text>
            <Field label="Application IDs" hint={PUBLISHING_APPLICATIONS_HINT}>
              <Input
                aria-label="Application IDs"
                value={publisherAppIds}
                disabled={!canManage}
                onChange={(_event, data) => setPublisherAppIds(data.value)}
              />
            </Field>
          </>
        )}

      {canConvert && (
        <>
          <Divider />
          <Text weight="semibold" size={400}>
            Privacy
          </Text>
          {!showConversion ? (
            <Button
              appearance="secondary"
              onClick={() => setShowConversion(true)}
            >
              Make workspace private
            </Button>
          ) : (
            <PrivateWorkspaceConversionSection
              workspace={workspace}
              onConverted={(converted) => {
                setWorkspace(converted);
                setPublisherAppIds(
                  converted.accessControl?.publisherAppIds.join(', ') ?? '',
                );
                setShowConversion(false);
                void refresh();
              }}
            />
          )}
        </>
      )}

      {saveError !== null && (
        <MessageBar intent="error">
          <MessageBarBody>{saveError}</MessageBarBody>
        </MessageBar>
      )}
      {hasConflict && (
        <Button
          appearance="secondary"
          onClick={() => {
            loadWorkspace();
          }}
        >
          Reload workspace
        </Button>
      )}

      <Toolbar className={styles.actions}>
        <Button appearance="secondary" onClick={close}>
          Close
        </Button>
        {!isConverting && (
          <Button
            appearance="primary"
            onClick={() => {
              void handleSave();
            }}
            disabled={!dirty || saving || hasConflict || !canManage}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        )}
      </Toolbar>

      <Divider />

      <Text className={styles.sectionTitle}>Danger zone</Text>
      <Text className={styles.dangerBody}>
        Deleting this workspace permanently removes all agents, graders,
        templates, runs, and results contained within it.
      </Text>
      <Toolbar className={styles.actions}>
        <Button
          appearance="secondary"
          icon={<DeleteIcon />}
          disabled={!canManage}
          onClick={() => setDeleteOpen(true)}
        >
          Delete workspace
        </Button>
      </Toolbar>

      <DeleteWorkspaceDialog
        workspace={workspace}
        open={deleteOpen}
        onDismiss={() => setDeleteOpen(false)}
        onDeleted={(name) => {
          void handleDeleted(name);
        }}
      />
    </Card>
  );
}
